import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe, SetupIntent, StripeElementLocale } from '@stripe/stripe-js'
import React, { useCallback, useState } from 'react'
import { FilledButton } from '../../../common/buttons/FilledButton'
import { translations } from '../../../../../utils/translations/translations-manager'
import { isValidStripeLanguage } from '../../../../../utils/stripe/stripe-locales'
import { useCentsStripePublicKey } from '../../../../../hooks/payments/CentsStripePublicKeyHook'

export const PaymentMethodCollection = ({
  success,
  failure,
  clientSecret,
}: {
  success: (setupIntent: string) => void
  failure: (e: unknown) => void
  clientSecret: string
}) => {
  const { get } = useCentsStripePublicKey()
  return (
    <Elements
      stripe={get().then(key =>
        loadStripe(key, { locale: (isValidStripeLanguage(translations.getLanguage()) ? translations.getLanguage() : 'en') as StripeElementLocale })
      )}
      options={{ clientSecret }}
    >
      <CollectPaymentMethodForm success={success} failure={failure} />
    </Elements>
  )
}

function CollectPaymentMethodForm({ success, failure }: { success: (setupIntent: string) => void; failure: (e: unknown) => void }): JSX.Element {
  const stripe = useStripe()
  const elements = useElements()
  const [complete, setComplete] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  const confirm = useCallback(async (): Promise<SetupIntent> => {
    if (!stripe || !elements) {
      throw new Error('Stripe has not loaded yet.')
    }
    const result = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    })

    if (result?.error) {
      throw result.error
    } else if (result.setupIntent.status !== 'succeeded') {
      throw new Error(`Unexpected state for setupIntent: ${result.setupIntent}`)
    }
    return result.setupIntent
  }, [stripe, elements])

  const submit = useCallback(async () => {
    try {
      setLoading(true)
      const setupIntent = await confirm()
      success(setupIntent.id)
    } catch (e) {
      failure(e)
    } finally {
      setLoading(false)
    }
  }, [stripe, elements, success, failure])

  return (
    <form onSubmit={submit}>
      <PaymentElement
        onChange={e => {
          setComplete(e.complete)
        }}
      />
      <div className="mt-4 flex justify-end">
        <FilledButton label={translations.texts.confirm.toString()} disabled={!complete} onClick={submit} loading={loading} />
      </div>
    </form>
  )
}
